import { loadQuizResult } from "../../clientViewRuntime/templates/buyerConciergeQuizUtils";
import { loadCasaMapaCompareIds } from "../casamap/casaMapaBuyerState";
import { loadCompareVehicleIds } from "./buyerCompareStorage";
import { loadGuestFinancingPrefs } from "./buyerFinancingPrefsStorage";
import { loadLikedVehicleIds } from "./buyerLikesStorage";

export const PENDING_GUEST_MERGE_KEY = "automap_pending_merge";

function readSessionJson(key) {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeSessionJson(key, value) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore quota errors
  }
}

function removeSessionKey(key) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export function extractFinancingPrefsFromIntent(intent) {
  if (!intent || typeof intent !== "object") return null;
  const prefs = {
    financingPreference: intent.financingPreference ?? null,
    downPayment: intent.downPayment ?? null,
    termMonths: intent.loanTermMonths ?? null,
    purchaseTimeline: intent.purchaseTimeline ?? null,
  };
  const hasValue = Object.values(prefs).some(
    (entry) => entry !== null && entry !== undefined && entry !== ""
  );
  return hasValue ? prefs : null;
}

export function loadCompareListingIds(slug) {
  const shared = loadCompareVehicleIds(slug);
  const casaMapa = loadCasaMapaCompareIds(slug);
  return [...new Set([...shared, ...casaMapa])].slice(0, 4);
}

export function buildGuestMergePayload(slug, visitorId, pending = null) {
  const quizResult = loadQuizResult(slug);
  const intent = quizResult?.intent || pending?.quizIntent || null;
  const compareListingIds = loadCompareListingIds(slug);
  const pendingCompare =
    Array.isArray(pending?.compareListingIds) && pending.compareListingIds.length
      ? pending.compareListingIds
      : [];

  return {
    visitorId: pending?.visitorId || visitorId || null,
    clientViewSlug: slug,
    timestamp: new Date().toISOString(),
    guestPayload: {
      likedListingIds: loadLikedVehicleIds(slug),
      // Anonymous vehicle/real-estate saved searches live in public_user_saved_searches
      // keyed by visitor_id. Server mergeAnonymousSavedSearchesToUser reassigns them;
      // no browser buffer is required for AutoMapa saved searches (Milestone 5).
      savedSearches: [],
      quizIntent: intent,
      financingPrefs:
        loadGuestFinancingPrefs(slug) ||
        extractFinancingPrefsFromIntent(intent) ||
        pending?.financingPrefs ||
        null,
      compareListingIds: compareListingIds.length ? compareListingIds : pendingCompare,
    },
  };
}

export function savePendingGuestMerge(slug, visitorId) {
  const built = buildGuestMergePayload(slug, visitorId);
  writeSessionJson(PENDING_GUEST_MERGE_KEY, {
    visitorId: built.visitorId,
    clientViewSlug: slug,
    likedListingIds: built.guestPayload.likedListingIds,
    quizIntent: built.guestPayload.quizIntent,
    financingPrefs: built.guestPayload.financingPrefs,
    compareListingIds: built.guestPayload.compareListingIds,
    timestamp: built.timestamp,
  });
}

export function readPendingGuestMerge() {
  return readSessionJson(PENDING_GUEST_MERGE_KEY);
}

export function clearPendingGuestMerge() {
  removeSessionKey(PENDING_GUEST_MERGE_KEY);
}

export function hasGuestMergeArtifacts(slug) {
  const pending = readPendingGuestMerge();
  if (pending?.clientViewSlug === slug) {
    return true;
  }
  if (loadLikedVehicleIds(slug).length) return true;
  if (loadQuizResult(slug)?.intent) return true;
  if (loadCompareListingIds(slug).length) return true;
  return false;
}

export function mergeSummaryHasChanges(merged = {}) {
  return (
    (merged.likes || 0) > 0 ||
    (merged.savedSearches || 0) > 0 ||
    merged.quizIntent ||
    merged.financingPrefs ||
    (merged.compareListingIds || 0) > 0
  );
}

export function shouldShowMergeRecoveryWarning(guestPayload, merged = {}, extras = {}) {
  const localLikeCount = guestPayload?.likedListingIds?.length || 0;
  if (localLikeCount === 0) return false;
  if ((merged.likes || 0) > 0) return false;
  // Auth already merges guest likes. A second pass that saves 0 new likes
  // usually means they are already on the account — not a lost-likes case.
  const invalidCount = Array.isArray(extras.invalidListingIds)
    ? extras.invalidListingIds.length
    : 0;
  return invalidCount > 0;
}
